'use client';

import { useState } from 'react';
import { RecordingModal } from '@/src/components/recording-modal';
import type { Example, IPASymbol, Sentence } from '@/src/types/ipa';
import { Button } from '@/src/components/ui/button';
import { useApiClient } from '@/src/hooks/use-api-client';
import { Loader2 } from 'lucide-react';

export function IpaSymbolClient({ ipaSymbol }: { ipaSymbol: IPASymbol }) {
  const apiClient = useApiClient();
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [currentScript, setCurrentScript] = useState('');
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | undefined>(
    undefined
  );
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);

  const handleTryClick = (text: string, audioUrl?: string) => {
    setCurrentScript(text);
    setCurrentAudioUrl(audioUrl);
    setIsRecordingModalOpen(true);
  };

  const playAudio = async (text: string, fallbackAudioUrl?: string) => {
    if (playingAudio === text) return;

    try {
      setPlayingAudio(text);

      // Call the TTS API
      const ttsResponse = await apiClient.getTextToSpeech(text);

      if (ttsResponse && ttsResponse.url) {
        const audio = new Audio(ttsResponse.url);
        audio.onended = () => setPlayingAudio(null);
        audio.onerror = () => {
          console.error('Error playing TTS audio');
          setPlayingAudio(null);
          // Fallback to original audio if available
          if (fallbackAudioUrl) {
            new Audio(fallbackAudioUrl).play();
          }
        };
        await audio.play();
      } else {
        console.error('Failed to get TTS URL');
        setPlayingAudio(null);
        // Fallback to original audio if available
        if (fallbackAudioUrl) {
          new Audio(fallbackAudioUrl).play();
        }
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setPlayingAudio(null);
      // Fallback to original audio if available
      if (fallbackAudioUrl) {
        new Audio(fallbackAudioUrl).play();
      }
    }
  };

  return (
    <>
      <div className='mx-auto px-4 py-8'>
        <h1 className='mb-6 text-3xl font-bold'>{ipaSymbol.symbol}</h1>

        <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
          <div>
            <h2 className='mb-4 text-xl font-semibold'>Description</h2>
            <p className='text-gray-700'>{ipaSymbol.type}</p>

            {ipaSymbol.videos?.lecture && (
              <div className='mt-6'>
                <h2 className='mb-4 text-xl font-semibold'>
                  Instruction Video
                </h2>
                <video
                  controls
                  className='w-full rounded-lg shadow'
                  src={ipaSymbol.videos.lecture.url}
                />
              </div>
            )}
          </div>

          <div>
            <div className='mb-8'>
              <h2 className='mb-4 text-xl font-semibold'>Example Words</h2>
              {ipaSymbol.examples?.map((example: Example, index: number) => (
                <div
                  key={index}
                  className='mb-4 overflow-hidden rounded-lg bg-white shadow'
                >
                  <div className='border-b border-gray-100 p-4'>
                    <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-center'>
                      <div>
                        <p className='font-medium'>{example.word}</p>
                        <p className='text-gray-500'>{example.ipa}</p>
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        <Button
                          onClick={() =>
                            playAudio(example.word, example.audio?.url)
                          }
                          variant='outline'
                          size='sm'
                          className='text-blue-600'
                          disabled={playingAudio === example.word}
                        >
                          {playingAudio === example.word ? (
                            <>
                              <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                              Playing...
                            </>
                          ) : (
                            'Listen'
                          )}
                        </Button>
                        <Button
                          onClick={() =>
                            handleTryClick(example.word, example.audio?.url)
                          }
                          variant='outline'
                          size='sm'
                          className='text-green-600'
                        >
                          Try
                        </Button>
                      </div>
                    </div>
                  </div>

                  {example.sentences?.map(
                    (sentence: Sentence, sentenceIndex: number) => (
                      <div
                        key={sentenceIndex}
                        className='border-b border-gray-100 p-4 last:border-b-0'
                      >
                        <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-center'>
                          <p className='font-medium'>{sentence.text}</p>
                          <div className='flex flex-wrap gap-2'>
                            <Button
                              onClick={() =>
                                playAudio(sentence.text, sentence.audio?.url)
                              }
                              variant='outline'
                              size='sm'
                              className='text-blue-600'
                              disabled={playingAudio === sentence.text}
                            >
                              {playingAudio === sentence.text ? (
                                <>
                                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                                  Playing...
                                </>
                              ) : (
                                'Listen'
                              )}
                            </Button>
                            <Button
                              onClick={() =>
                                handleTryClick(
                                  sentence.text,
                                  sentence.audio?.url
                                )
                              }
                              variant='outline'
                              size='sm'
                              className='text-green-600'
                            >
                              Try
                            </Button>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <RecordingModal
          isOpen={isRecordingModalOpen}
          onClose={() => setIsRecordingModalOpen(false)}
          script={currentScript}
          audioUrl={currentAudioUrl}
        />
      </div>
    </>
  );
}
